import { createFileRoute } from "@tanstack/react-router";
import { PageHeader } from "@/components/page-header";
import { DateRangeFilter } from "@/components/date-range-filter";
import { Panel } from "@/components/analytics-ui";
import { SkAnalyticsPage } from "@/components/skeleton";
import { PageQuery } from "@/components/page-query";
import { AdExplorer } from "@/features/meta/ad-explorer";
import type { MetaPage } from "@shared/pages/meta";
import { pageQuery } from "@/lib/queries";
import { useTitle } from "@/lib/title";

// Meta -> Ad Performance: every individual ad ranked by spend,
// winners called out, and click-to-expand: video ads play inline in a
// lightbox (AdExplorer). Ad-level data rides the campaigns visibility toggle.

export const Route = createFileRoute("/_app/meta/ads")({
  loaderDeps: ({ search }) => search,
  loader: ({ context, deps }) => {
    void context.queryClient.prefetchQuery(pageQuery<MetaPage>("meta-ads", deps));
  },
  component: MetaAdsRoute,
});

const usd = (n: number) =>
  n.toLocaleString("en-US", { style: "currency", currency: "USD", maximumFractionDigits: n >= 100 ? 0 : 2 });
const num = (n: number) => n.toLocaleString("en-US");
const pct = (n: number) => `${(n * 100).toFixed(2)}%`;

function MetaAdsRoute() {
  useTitle("Ad performance");
  const search = Route.useSearch();
  return (
    <PageQuery<MetaPage> name="meta-ads" search={search} skeleton={<SkAnalyticsPage />}>
      {(page) => <MetaAdPerformance page={page} />}
    </PageQuery>
  );
}

function MetaAdPerformance({ page }: { page: MetaPage }) {
  const { configured, report: live, presets } = page;

  // No sample data, ever: absent feed or a hidden section renders a plain
  // explanation panel (the sidebar hides this page in that case anyway).
  if (!configured || !live || !live.ads?.adPerformance) {
    return (
      <div className="page-shell">
        <PageHeader title="Ad performance" />
        <Panel title={!configured || !live ? "Not connected" : "Not enabled"} className="mt-5">
          <p className="text-sm text-muted">
            {!configured || !live
              ? "Meta numbers show here once Digital King connects the Meta account."
              : "Ad-level detail is enabled per account by the agency. It will appear here automatically once it's switched on in the agency hub."}
          </p>
        </Panel>
      </div>
    );
  }
  const ads = live.ads.adPerformance.filter((a) => a.impressions > 0 || (a.spend ?? 0) > 0);

  const showSpend = ads.some((a) => a.spend !== undefined);
  const showConv = ads.some((a) => a.conversions !== undefined);

  const withConv = ads.filter((a) => (a.conversions ?? 0) > 0 && (a.spend ?? 0) > 0);
  const bestValue = withConv.length
    ? [...withConv].sort((a, b) => (a.spend ?? 0) / a.conversions! - (b.spend ?? 0) / b.conversions!)[0]
    : null;
  const mostLeads = showConv && ads.length
    ? [...ads].sort((a, b) => (b.conversions ?? 0) - (a.conversions ?? 0))[0]
    : null;
  const bestCtr = ads.filter((a) => a.impressions > 1000).sort(
    (a, b) => b.clicks / b.impressions - a.clicks / a.impressions,
  )[0];

  const winners = [
    ...(mostLeads && (mostLeads.conversions ?? 0) > 0
      ? [{ tag: "Most conversions", id: mostLeads.id, stat: num(Math.round(mostLeads.conversions ?? 0)), sub: mostLeads.campaign }]
      : []),
    ...(bestValue
      ? [{ tag: "Best cost / conv.", id: bestValue.id, stat: usd((bestValue.spend ?? 0) / bestValue.conversions!), sub: bestValue.campaign }]
      : []),
    ...(bestCtr
      ? [{ tag: "Best click rate", id: bestCtr.id, stat: pct(bestCtr.clicks / bestCtr.impressions), sub: bestCtr.campaign }]
      : []),
  ];

  return (
    <div className="page-shell">
      <div className="flex flex-wrap items-start justify-between gap-3">
        <PageHeader title="Ad performance" info="Every ad, ranked by spend. Click one to expand it." />
        <DateRangeFilter presets={presets} defaultKey="30d" />
      </div>

      <AdExplorer ads={ads} winners={winners} showSpend={showSpend} showConv={showConv} />
    </div>
  );
}
